import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BarChart3,
  Star,
  Mail,
  Users,
  Activity,
  Loader2,
  Lock,
  Clock,
  RefreshCw,
} from "lucide-react";
import { fmtDate, colorFor } from "@/lib/corpsColors";

export default function Admin() {
  const [sp] = useSearchParams();
  const key = sp.get("key") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    axios
      .get(`${API}/admin/stats`, { params: { key } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Erreur"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (key) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!key) {
    return (
      <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="admin-no-key">
        <Lock className="w-10 h-10 mx-auto text-slate-400" />
        <h1 className="font-display text-2xl font-bold text-[#0F2137] mt-4">
          Accès administrateur
        </h1>
        <p className="text-sm text-slate-500 mt-2">
          Cette page requiert une clé. Ajoutez <code className="bg-slate-100 px-1 rounded">?key=VOTRE_CLE</code> à l'URL.
        </p>
      </div>
    );
  }

  if (loading)
    return (
      <div className="text-center py-20">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#0F2137]" />
      </div>
    );

  if (error)
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center">
        <Lock className="w-10 h-10 mx-auto text-red-400" />
        <h1 className="font-display text-2xl font-bold text-[#0F2137] mt-4">
          {error}
        </h1>
      </div>
    );

  const t = data.totals;
  const fb = data.feedback;
  const maxCorpsCount = Math.max(1, ...data.top_corps.map((c) => c.count));

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="font-mono-tech text-xs uppercase tracking-wider text-slate-500">
            Tableau de bord
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0F2137] mt-1">
            Admin ChantiPlanning
          </h1>
        </div>
        <Button variant="outline" onClick={load} data-testid="btn-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Actualiser
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <Kpi label="Total plannings" value={t.projects} icon={BarChart3} />
        <Kpi label="24h" value={t.generated_24h} accent />
        <Kpi label="7 jours" value={t.generated_7d} accent />
        <Kpi label="30 jours" value={t.generated_30d} accent />
        <Kpi label="Emails collectés" value={t.with_email} icon={Mail} />
        <Kpi label="Avec retour" value={t.with_feedback} icon={Star} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Feedback */}
        <section className="rounded-xl border border-slate-200 bg-white p-6" data-testid="feedback-block">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg text-[#0F2137]">
              Retours clients
            </h2>
            <div className="flex items-baseline gap-1">
              <span className="font-display text-3xl font-extrabold text-[#F59E0B]">
                {fb.average_rating || "—"}
              </span>
              <span className="text-sm text-slate-500">/5</span>
              <span className="text-xs text-slate-400 ml-2">({fb.count})</span>
            </div>
          </div>
          {/* Distribution */}
          <div className="space-y-1.5">
            {[5, 4, 3, 2, 1].map((n) => {
              const count = fb.distribution[String(n)] || 0;
              const pct = fb.count ? (count / fb.count) * 100 : 0;
              return (
                <div key={n} className="flex items-center gap-2 text-xs">
                  <span className="w-6 text-slate-500 font-mono-tech">{n}★</span>
                  <div className="flex-1 h-2 bg-slate-100 rounded">
                    <div
                      className="h-full rounded bg-[#F59E0B]"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-8 text-right font-mono-tech text-slate-500">{count}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-6 space-y-3 max-h-96 overflow-y-auto">
            {fb.recent.length === 0 && (
              <div className="text-sm text-slate-400 text-center py-6">
                Aucun retour pour l'instant.
              </div>
            )}
            {fb.recent.map((f, i) => (
              <div key={i} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#F59E0B]">{"★".repeat(f.rating)}<span className="text-slate-200">{"★".repeat(5 - f.rating)}</span></span>
                  <span className="text-slate-400 font-mono-tech">
                    {new Date(f.at).toLocaleDateString("fr-FR")}
                  </span>
                  {f.email && (
                    <span className="text-slate-500 truncate">· {f.email}</span>
                  )}
                </div>
                {f.comment && (
                  <div className="text-sm text-slate-700 mt-1.5">{f.comment}</div>
                )}
                <Link
                  to={`/projet/${f.project_id}`}
                  className="text-xs text-[#B45309] hover:underline mt-1 inline-block"
                >
                  → {f.project_name}
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Top corps d'état */}
        <section className="rounded-xl border border-slate-200 bg-white p-6" data-testid="corps-block">
          <h2 className="font-display font-bold text-lg text-[#0F2137] mb-4">
            Corps d'état les plus utilisés
          </h2>
          {data.top_corps.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-6">Aucune donnée.</div>
          ) : (
            <div className="space-y-2.5">
              {data.top_corps.map((c) => (
                <div key={c.name} className="flex items-center gap-3">
                  <span
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: colorFor(c.name) }}
                  />
                  <span className="flex-1 text-sm text-slate-700 truncate">{c.name}</span>
                  <div className="w-32 h-2 bg-slate-100 rounded overflow-hidden">
                    <div
                      className="h-full rounded"
                      style={{
                        width: `${(c.count / maxCorpsCount) * 100}%`,
                        background: colorFor(c.name),
                      }}
                    />
                  </div>
                  <span className="w-8 text-right font-mono-tech text-xs text-slate-500">
                    {c.count}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-slate-100">
            <h3 className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-500 mb-2">
              Répartition par type
            </h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.project_types).map(([k, v]) => (
                <Badge key={k} variant="secondary" className="capitalize">
                  {k} · {v}
                </Badge>
              ))}
            </div>
          </div>
        </section>

        {/* Emails */}
        <section className="rounded-xl border border-slate-200 bg-white p-6" data-testid="emails-block">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-lg text-[#0F2137]">
              Emails collectés
            </h2>
            <Badge className="bg-[#0F2137] text-white hover:bg-[#0F2137]">
              {data.emails.length}
            </Badge>
          </div>
          {data.emails.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-6">
              Aucun email pour l'instant.
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto text-sm font-mono-tech space-y-1">
              {data.emails.map((e) => (
                <div key={e} className="text-slate-700 py-0.5 border-b border-slate-50 last:border-0">
                  {e}
                </div>
              ))}
              <button
                onClick={() => navigator.clipboard.writeText(data.emails.join("\n"))}
                data-testid="btn-copy-emails"
                className="mt-3 text-xs text-[#B45309] hover:underline"
              >
                Copier tous les emails
              </button>
            </div>
          )}
        </section>

        {/* Recent projects */}
        <section className="rounded-xl border border-slate-200 bg-white p-6" data-testid="recent-block">
          <h2 className="font-display font-bold text-lg text-[#0F2137] mb-4">
            Derniers plannings générés
          </h2>
          {data.recent_projects.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-6">Aucun projet.</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {data.recent_projects.map((p) => (
                <Link
                  key={p.id}
                  to={`/projet/${p.id}`}
                  className="block rounded-lg border border-slate-200 p-3 hover:border-[#F59E0B] transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-sm text-[#0F2137] truncate">{p.name}</div>
                    {p.has_feedback && (
                      <span className="text-[#F59E0B] text-xs">
                        {"★".repeat(p.rating || 0)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 font-mono-tech">
                    <Clock className="w-3 h-3" />
                    {p.created_at && fmtDate(new Date(p.created_at))}
                    <span>·</span>
                    <span>{p.project_type}</span>
                    {p.surface_m2 && (<><span>·</span><span>{p.surface_m2} m²</span></>)}
                    <span>·</span>
                    <span>{p.total_duration_days} j</span>
                  </div>
                  {p.user_email && (
                    <div className="text-xs text-slate-400 mt-0.5">{p.user_email}</div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="mt-8 text-center text-xs text-slate-400 font-mono-tech">
        Rechargez la page pour actualiser · Accès protégé par clé admin
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className={`rounded-xl border p-4 ${accent ? "bg-[#0F2137] text-white border-[#0F2137]" : "bg-white border-slate-200"}`}>
      <div className="flex items-center justify-between">
        <div className={`font-mono-tech text-[10px] uppercase tracking-wider ${accent ? "text-slate-300" : "text-slate-500"}`}>
          {label}
        </div>
        {Icon && <Icon className={`w-3.5 h-3.5 ${accent ? "text-[#F59E0B]" : "text-slate-400"}`} />}
      </div>
      <div className={`font-display text-2xl font-extrabold mt-1 ${accent ? "text-white" : "text-[#0F2137]"}`}>
        {value}
      </div>
    </div>
  );
}
